/**
 * Cloudflare Turnstile client helper.
 *
 * Sensitive actions (eligibility re-check, claims, paid spinner / marketplace
 * $OBOL / Gold Exchange order creation) fetch a fresh single-use token here
 * and send it as the `x-turnstile-token` request header; the backend verifies
 * it server-side (the widget alone proves nothing).
 *
 * UX contract:
 * - When Cloudflare does NOT require interaction the flow is invisible — no
 *   overlay, no flash, the token resolves silently.
 * - When Cloudflare DOES require interaction, a centered modal overlay
 *   ("Security check required to continue.") shows the challenge front and
 *   center (never a hidden strip at the page bottom), with a Cancel button.
 *   It reuses the app's `.modal-backdrop`/`.modal-card` styling and lives on
 *   document.body, so the app's innerHTML re-renders can't destroy it.
 * - One challenge at a time: concurrent protected actions are serialized.
 * - Tokens are single-use and never reused across actions: every getToken()
 *   renders a fresh widget and disposes it (success or failure) afterwards.
 *
 * Without a configured site key (local dev, tests) the Noop provider returns
 * `null` and requests are sent without the header — matching the backend's
 * `off`/`shadow` modes. Only the PUBLIC site key ever reaches this bundle;
 * the secret key is backend-only.
 */

export interface TurnstileTokenProvider {
  /** Resolve a fresh token for one protected action, or null when Turnstile is not configured. */
  getToken(action: string): Promise<string | null>;
}

export class NoopTurnstileProvider implements TurnstileTokenProvider {
  async getToken(): Promise<null> {
    return null;
  }
}

/** User-facing copy (assert against these in tests; keep in sync with backend copy). */
export const TURNSTILE_COPY = {
  required: 'Security check required to continue.',
  unavailable: 'Security check unavailable. Please try again.',
  expired: 'Security check expired. Please try again.',
  failed: 'Security check failed. Please try again.',
  cancelled: 'Security check cancelled.',
} as const;

export interface TurnstileGlobal {
  render(el: HTMLElement, params: Record<string, unknown>): string;
  execute(widgetId: string): void;
  remove(widgetId: string): void;
}

/** Environment seam so the full challenge lifecycle is testable without a browser. */
export interface TurnstileEnv {
  getDocument(): Document | null;
  getTurnstile(): TurnstileGlobal | undefined;
  /** Load the Turnstile api.js (rejects when blocked/unreachable). */
  loadScript(doc: Document): Promise<void>;
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const CHALLENGE_TIMEOUT_MS = 120_000;

function defaultEnv(): TurnstileEnv {
  return {
    getDocument: () => (typeof document === 'undefined' ? null : document),
    getTurnstile: () => (globalThis as { turnstile?: TurnstileGlobal }).turnstile,
    loadScript: (doc) =>
      new Promise<void>((resolve, reject) => {
        const script = doc.createElement('script');
        script.src = SCRIPT_SRC;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(TURNSTILE_COPY.unavailable));
        doc.head.appendChild(script);
      }),
  };
}

export interface ChallengeUi {
  backdrop: HTMLElement;
  slot: HTMLElement;
  /** Make the challenge visible (called only when Cloudflare requires interaction). */
  show(): void;
  onCancel(handler: () => void): void;
}

/**
 * Centered challenge overlay. Hidden until Cloudflare actually needs the user
 * (interaction-only appearance), then shown as a front-and-center modal.
 * Exported for tests.
 */
export function buildChallengeUi(doc: Document): ChallengeUi {
  const backdrop = doc.createElement('div');
  backdrop.className = 'modal-backdrop turnstile-backdrop';
  backdrop.style.display = 'none';

  const card = doc.createElement('div');
  card.className = 'modal-card turnstile-card';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.setAttribute('aria-label', 'Security check');

  const title = doc.createElement('h2');
  title.textContent = 'Security check';
  const note = doc.createElement('p');
  note.textContent = TURNSTILE_COPY.required;

  const slot = doc.createElement('div');
  slot.className = 'turnstile-slot';

  const buttonRow = doc.createElement('div');
  buttonRow.className = 'btn-row turnstile-btn-row';
  const cancel = doc.createElement('button');
  cancel.type = 'button';
  cancel.textContent = 'Cancel';

  buttonRow.appendChild(cancel);
  card.appendChild(title);
  card.appendChild(note);
  card.appendChild(slot);
  card.appendChild(buttonRow);
  backdrop.appendChild(card);

  return {
    backdrop,
    slot,
    show: () => {
      backdrop.style.display = 'flex';
    },
    onCancel: (handler) => {
      cancel.addEventListener('click', handler);
    },
  };
}

export class CloudflareTurnstileProvider implements TurnstileTokenProvider {
  private scriptPromise: Promise<void> | null = null;
  private pending: Promise<string | null> | null = null;

  constructor(
    private readonly siteKey: string,
    private readonly env: TurnstileEnv = defaultEnv(),
  ) {}

  getToken(action: string): Promise<string | null> {
    // Serialize: one widget/challenge at a time; a failure of the previous
    // action never poisons the next one.
    const next = (this.pending ?? Promise.resolve(null))
      .catch(() => null)
      .then(() => this.solve(action));
    this.pending = next;
    return next;
  }

  private async solve(action: string): Promise<string | null> {
    const doc = this.env.getDocument();
    if (!doc) return null; // non-browser (tests) — graceful untokened path
    await this.ensureScript(doc);
    const turnstile = this.env.getTurnstile();
    if (!turnstile) throw new Error(TURNSTILE_COPY.unavailable);

    const ui = buildChallengeUi(doc);
    doc.body.appendChild(ui.backdrop);

    let widgetId: string | null = null;
    try {
      return await new Promise<string>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(TURNSTILE_COPY.failed)), CHALLENGE_TIMEOUT_MS);
        const settle = (fn: () => void) => {
          clearTimeout(timer);
          fn();
        };
        ui.onCancel(() => settle(() => reject(new Error(TURNSTILE_COPY.cancelled))));
        widgetId = turnstile.render(ui.slot, {
          sitekey: this.siteKey,
          action,
          appearance: 'interaction-only',
          execution: 'execute',
          'response-field': false,
          callback: (token: string) => settle(() => resolve(token)),
          'error-callback': () => settle(() => reject(new Error(TURNSTILE_COPY.failed))),
          'expired-callback': () => settle(() => reject(new Error(TURNSTILE_COPY.expired))),
          'timeout-callback': () => settle(() => reject(new Error(TURNSTILE_COPY.failed))),
          // Cloudflare is about to show a visible challenge: surface the modal.
          'before-interactive-callback': () => ui.show(),
        });
        turnstile.execute(widgetId);
      });
    } finally {
      // Single-use: dispose the widget and overlay whether we succeeded,
      // failed, or were cancelled — the next action always starts fresh.
      if (widgetId !== null) {
        try {
          turnstile.remove(widgetId);
        } catch {
          /* widget already gone */
        }
      }
      ui.backdrop.remove();
    }
  }

  private ensureScript(doc: Document): Promise<void> {
    if (this.env.getTurnstile()) return Promise.resolve();
    if (!this.scriptPromise) {
      this.scriptPromise = this.env.loadScript(doc).catch((err: unknown) => {
        this.scriptPromise = null; // allow a retry on the next action
        throw err instanceof Error ? err : new Error(TURNSTILE_COPY.unavailable);
      });
    }
    return this.scriptPromise;
  }
}
